// src/runtimes/local/LocalThreadListRuntimeCore.tsx
import { generateId } from "../../utils/idUtils.mjs";
var RESOLVED_PROMISE = Promise.resolve();
var LocalThreadListRuntimeCore = class {
  constructor(_threadFactory) {
    this._threadFactory = _threadFactory;
    this.switchToNewThread();
  }
  _threadData = /* @__PURE__ */ new Map();
  _threadIds = [];
  _archivedThreadIds = [];
  _newThreadId;
  get newThreadId() {
    return this._newThreadId;
  }
  get threadIds() {
    return this._threadIds;
  }
  get archivedThreadIds() {
    return this._archivedThreadIds;
  }
  _mainThreadId;
  get mainThreadId() {
    return this._mainThreadId;
  }
  getMainThreadRuntimeCore() {
    const result = this._threadData.get(this._mainThreadId)?.runtime;
    if (!result)
      throw new Error("Main thread not found. This is a bug in assistant-ui.");
    return result;
  }
  getLoadThreadsPromise() {
    return RESOLVED_PROMISE;
  }
  getItemById(threadId) {
    return this._threadData.get(threadId);
  }
  async switchToThread(threadId) {
    if (this._mainThreadId === threadId) return;
    const data = this._threadData.get(threadId);
    if (!data) throw new Error("Thread not found");
    if (data.status === "archived") await this.unarchive(threadId);
    this._mainThreadId = data.threadId;
    this._notifySubscribers();
  }
  switchToNewThread() {
    if (this._newThreadId === void 0) {
      let threadId;
      do {
        threadId = generateId();
      } while (this._threadData.has(threadId));
      const runtime = this._threadFactory();
      const dispose = runtime.unstable_on("initialize", () => {
        dispose();
        const data = this._threadData.get(threadId);
        if (!data) throw new Error("Thread not found");
        this._stateOp(threadId, "regular");
      });
      this._threadData.set(threadId, {
        runtime,
        status: "new",
        threadId
      });
      this._newThreadId = threadId;
    }
    this.switchToThread(this._newThreadId);
    return Promise.resolve();
  }
  async _stateOp(threadId, newState) {
    const data = this._threadData.get(threadId);
    if (!data) throw new Error("Thread not found");
    const { status: lastState } = data;
    if (lastState === newState) return;
    switch (lastState) {
      case "new":
        this._newThreadId = void 0;
        break;
      case "regular":
        this._threadIds = this._threadIds.filter((t) => t !== threadId);
        break;
      case "archived":
        this._archivedThreadIds = this._archivedThreadIds.filter(
          (t) => t !== threadId
        );
        break;
      default: {
        const _exhaustiveCheck = lastState;
        throw new Error(`Unsupported state: ${_exhaustiveCheck}`);
      }
    }
    switch (newState) {
      case "regular":
        this._threadIds = [...this._threadIds, data.threadId];
        break;
      case "archived":
        this._archivedThreadIds = [...this._archivedThreadIds, data.threadId];
        break;
      case "deleted":
        this._threadData.delete(threadId);
        break;
      default: {
        const _exhaustiveCheck = newState;
        throw new Error(`Unsupported state: ${_exhaustiveCheck}`);
      }
    }
    if (newState !== "deleted") {
      this._threadData.set(threadId, {
        ...data,
        status: newState
      });
    }
    if (threadId === this._mainThreadId && (newState === "archived" || newState === "deleted")) {
      const lastThreadId = this._threadIds[0];
      if (lastThreadId) {
        await this.switchToThread(lastThreadId);
      } else {
        await this.switchToNewThread();
      }
    } else {
      this._notifySubscribers();
    }
  }
  rename(threadId, newTitle) {
    const data = this._threadData.get(threadId);
    if (!data) throw new Error("Thread not found");
    this._threadData.set(threadId, {
      ...data,
      title: newTitle
    });
    this._notifySubscribers();
    return Promise.resolve();
  }
  archive(threadId) {
    const data = this._threadData.get(threadId);
    if (!data) throw new Error("Thread not found");
    if (data.status !== "regular")
      throw new Error("Thread is not yet initialized or already archived");
    this._stateOp(threadId, "archived");
    return Promise.resolve();
  }
  unarchive(threadId) {
    const data = this._threadData.get(threadId);
    if (!data) throw new Error("Thread not found");
    if (data.status !== "archived") throw new Error("Thread is not archived");
    this._stateOp(threadId, "regular");
    return Promise.resolve();
  }
  delete(threadId) {
    const data = this._threadData.get(threadId);
    if (!data) throw new Error("Thread not found");
    if (data.status !== "regular" && data.status !== "archived")
      throw new Error("Thread is not yet initialized");
    this._stateOp(threadId, "deleted");
    return Promise.resolve();
  }
  _subscriptions = /* @__PURE__ */ new Set();
  subscribe(callback) {
    this._subscriptions.add(callback);
    return () => this._subscriptions.delete(callback);
  }
  _notifySubscribers() {
    for (const callback of this._subscriptions) callback();
  }
};
export {
  LocalThreadListRuntimeCore
};
//# sourceMappingURL=LocalThreadListRuntimeCore.mjs.map